import { BContent, BLayout, BSider } from "components/base";
import ChatWindow from "components/chat/ChatWindow";
import MessageComposer from "components/chat/MessageComposer";
import SearchChatModal from "components/search/SearchChatModal";
import Sidebar from "components/sidebar/Sidebar";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { chatStore } from "store/chatStore";
import { userStore } from "store/userStore";
import type { Staff } from "types/staff";
import "./MainLayout.scss";

const OVERLAY_TRANSITION_MS = 280;

const MainLayout = observer(function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [overlayMounted, setOverlayMounted] = useState(false);
  const [overlayActive, setOverlayActive] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const isOverlayOpen = isMobile && !sidebarCollapsed;
  const layoutCollapsed = isMobile || sidebarCollapsed;

  useEffect(() => {
    if (!userStore.token) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await userStore.getProfile();
        if (cancelled) {
          return;
        }
        chatStore.initFromProfile(userStore.info as Staff);
        if (chatStore.selectedProjectId) {
          await chatStore.fetchConversations(chatStore.selectedProjectId);
        }
      } catch {
        // request interceptor already shows error toast
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isOverlayOpen) {
      setOverlayMounted(true);
      const frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setOverlayActive(true);
        });
      });
      return () => cancelAnimationFrame(frameId);
    }

    setOverlayActive(false);
    const timerId = window.setTimeout(() => {
      setOverlayMounted(false);
    }, OVERLAY_TRANSITION_MS);

    return () => window.clearTimeout(timerId);
  }, [isOverlayOpen]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const handleBreakpoint = useCallback((broken: boolean) => {
    setIsMobile(broken);
    if (broken) {
      setSidebarCollapsed(true);
    }
  }, []);

  const handleExpandSidebar = useCallback(() => {
    setSidebarCollapsed(false);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setSidebarCollapsed(true);
  }, []);

  const handleOpenSearch = useCallback(() => {
    setSearchOpen(true);
  }, []);

  const handleCloseSearch = useCallback(() => {
    setSearchOpen(false);
  }, []);

  return (
    <BLayout className="main-layout">
      <BSider
        width={260}
        collapsedWidth={52}
        collapsible
        collapsed={layoutCollapsed}
        onCollapse={setSidebarCollapsed}
        onBreakpoint={handleBreakpoint}
        trigger={null}
        className={clsx("main-layout__sider", "b-sider")}
        breakpoint="lg"
      >
        <Sidebar
          collapsed={isMobile ? true : sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          onExpandSidebar={handleExpandSidebar}
          onOpenSearch={handleOpenSearch}
        />
      </BSider>
      {overlayMounted ? (
        <>
          <button
            type="button"
            className={clsx(
              "main-layout__backdrop",
              overlayActive && "main-layout__backdrop--open",
            )}
            aria-label="Đóng thanh bên"
            onClick={handleCloseOverlay}
          />
          <aside
            className={clsx(
              "main-layout__overlay-panel",
              "b-sider",
              overlayActive && "main-layout__overlay-panel--open",
            )}
          >
            <Sidebar
              collapsed={false}
              onToggleCollapse={handleToggleSidebar}
              onExpandSidebar={handleExpandSidebar}
              onOpenSearch={handleOpenSearch}
              onNavigate={handleCloseOverlay}
            />
          </aside>
        </>
      ) : null}
      <BLayout className="main-layout__inner">
        <BContent className="main-layout__content">
          <div className="main-layout__chat">
            <ChatWindow />
          </div>
          <div className="main-layout__composer">
            <MessageComposer />
          </div>
        </BContent>
      </BLayout>
      <SearchChatModal open={searchOpen} onClose={handleCloseSearch} />
    </BLayout>
  );
});

export default MainLayout;
